import React, { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import "./Home.css";
import { FaCogs, FaShieldAlt } from "react-icons/fa";
import { FaChartLine, FaChartPie } from "react-icons/fa6";

const features = [
  {
    icon: <FaCogs />,
    title: "Automated Algorithms",
    description: "Set up trading rules once and let RoboVest execute them automatically based on real-time market data.",
  },
  {
    icon: <FaChartLine />,
    title: "Portfolio Monitoring",
    description: "Track your positions, orders, and portfolio equity in real time from a single dashboard.",
  },
  {
    icon: <FaChartPie />,
    title: "Multiple Strategies",
    description: "Choose from Limit, Limit Percent, or Lookback strategies — or combine them across different tickers.",
  },
  {
    icon: <FaShieldAlt />,
    title: "Secure & Reliable",
    description: "Built on AWS with Cognito authentication and encrypted API connections to keep your data safe.",
  },
];

const steps = [
  { number: 1, title: "Sign Up", description: "Create your account with a secure Cognito login in seconds." },
  { number: 2, title: "Configure Strategy", description: "Pick your stocks, set buy/sell rules, and define your risk parameters." },
  { number: 3, title: "Auto-Trade", description: "RoboVest monitors the market and places trades for you automatically." },
];

const TICKER_ITEMS = [
  "AAPL +2.4%", "TSLA +5.1%", "NVDA +8.3%", "MSFT +1.2%",
  "AMZN +3.7%", "META +4.9%", "GOOGL +2.1%", "SPY +1.8%",
  "QQQ +2.9%", "BRK.B +0.7%", "PLTR +11.2%", "AMD +6.4%",
];

const Home = () => {
  const canvasRef = useRef(null);
  const cursorDotRef = useRef(null);
  const cursorRingRef = useRef(null);
  const animFrameRef = useRef(null);
  const particlesRef = useRef([]);
  const mouseRef = useRef({ x: -2000, y: -2000 });

  // Particle canvas
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    const resize = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };
    resize();
    window.addEventListener("resize", resize);

    const onMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      mouseRef.current = { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    window.addEventListener("mousemove", onMouseMove);

    particlesRef.current = Array.from({ length: 90 }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      vx: (Math.random() - 0.5) * 0.35,
      vy: (Math.random() - 0.5) * 0.35,
      r: Math.random() * 1.8 + 0.6,
      opacity: Math.random() * 0.5 + 0.3,
    }));

    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      const { x: mx, y: my } = mouseRef.current;

      particlesRef.current.forEach((p) => {
        p.x += p.vx;
        p.y += p.vy;
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        const dx = p.x - mx;
        const dy = p.y - my;
        const dist = Math.sqrt(dx * dx + dy * dy);
        if (dist < 130 && dist > 0) {
          p.x += (dx / dist) * 1.8;
          p.y += (dy / dist) * 1.8;
        }

        ctx.beginPath();
        ctx.arc(p.x, p.y, p.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(184,255,60,${p.opacity})`;
        ctx.fill();
      });

      const pts = particlesRef.current;
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const dx = pts[i].x - pts[j].x;
          const dy = pts[i].y - pts[j].y;
          const dist = Math.sqrt(dx * dx + dy * dy);
          if (dist < 110) {
            ctx.beginPath();
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            const alpha = 0.12 * (1 - dist / 110);
            ctx.strokeStyle = `rgba(184,255,60,${alpha})`;
            ctx.lineWidth = 0.6;
            ctx.stroke();
          }
        }
      }

      animFrameRef.current = requestAnimationFrame(draw);
    };
    draw();

    return () => {
      cancelAnimationFrame(animFrameRef.current);
      window.removeEventListener("resize", resize);
      window.removeEventListener("mousemove", onMouseMove);
    };
  }, []);

  // Custom cursor
  useEffect(() => {
    const dot = cursorDotRef.current;
    const ring = cursorRingRef.current;
    if (!dot || !ring) return;

    let cx = 0, cy = 0, rx = 0, ry = 0;
    let rafId;

    const onMove = (e) => { cx = e.clientX; cy = e.clientY; };
    window.addEventListener("mousemove", onMove);

    const animate = () => {
      rx += (cx - rx) * 0.11;
      ry += (cy - ry) * 0.11;
      dot.style.transform = `translate(${cx - 4}px, ${cy - 4}px)`;
      ring.style.transform = `translate(${rx - 22}px, ${ry - 22}px)`;
      rafId = requestAnimationFrame(animate);
    };
    animate();

    const hoverEls = document.querySelectorAll("a, button, .tilt-card, .step-item");
    const enter = (e) => {
      ring.classList.add("cursor-hover");
      if (e.currentTarget.classList.contains("btn-prime")) ring.classList.add("cursor-prime");
    };
    const leave = () => ring.classList.remove("cursor-hover", "cursor-prime");
    hoverEls.forEach((el) => { el.addEventListener("mouseenter", enter); el.addEventListener("mouseleave", leave); });

    document.body.classList.add("custom-cursor-active");

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("mousemove", onMove);
      hoverEls.forEach((el) => { el.removeEventListener("mouseenter", enter); el.removeEventListener("mouseleave", leave); });
      document.body.classList.remove("custom-cursor-active");
    };
  }, []);

  // Scroll reveal
  useEffect(() => {
    const els = document.querySelectorAll(".rv-reveal");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) { e.target.classList.add("rv-revealed"); obs.unobserve(e.target); }
      });
    }, { threshold: 0.08 });
    els.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // Animated counters
  useEffect(() => {
    const counters = document.querySelectorAll(".rv-counter");
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        const target = parseFloat(el.dataset.target);
        const prefix = el.dataset.prefix || "";
        const suffix = el.dataset.suffix || "";
        const decimals = parseInt(el.dataset.decimals || "0");
        let startTime = null;
        const duration = 2200;
        const step = (ts) => {
          if (!startTime) startTime = ts;
          const progress = Math.min((ts - startTime) / duration, 1);
          const eased = 1 - Math.pow(1 - progress, 4);
          el.textContent = prefix + (eased * target).toFixed(decimals) + suffix;
          if (progress < 1) requestAnimationFrame(step);
        };
        requestAnimationFrame(step);
        obs.unobserve(el);
      });
    }, { threshold: 0.3 });
    counters.forEach((el) => obs.observe(el));
    return () => obs.disconnect();
  }, []);

  // 3D tilt on feature cards
  useEffect(() => {
    const cards = document.querySelectorAll(".tilt-card");
    const onMove = (e) => {
      const card = e.currentTarget;
      const rect = card.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width - 0.5;
      const y = (e.clientY - rect.top) / rect.height - 0.5;
      card.style.transform = `perspective(700px) rotateY(${x * 14}deg) rotateX(${-y * 14}deg) translateZ(12px)`;
    };
    const onLeave = (e) => {
      e.currentTarget.style.transform = "perspective(700px) rotateY(0deg) rotateX(0deg) translateZ(0px)";
    };
    cards.forEach((c) => { c.addEventListener("mousemove", onMove); c.addEventListener("mouseleave", onLeave); });
    return () => cards.forEach((c) => { c.removeEventListener("mousemove", onMove); c.removeEventListener("mouseleave", onLeave); });
  }, []);

  // Magnetic buttons
  useEffect(() => {
    const btns = document.querySelectorAll(".magnetic");
    const onMove = (e) => {
      const btn = e.currentTarget;
      const rect = btn.getBoundingClientRect();
      const x = (e.clientX - rect.left - rect.width / 2) * 0.28;
      const y = (e.clientY - rect.top - rect.height / 2) * 0.28;
      btn.style.transform = `translate(${x}px, ${y}px)`;
    };
    const onLeave = (e) => { e.currentTarget.style.transform = "translate(0,0)"; };
    btns.forEach((b) => { b.addEventListener("mousemove", onMove); b.addEventListener("mouseleave", onLeave); });
    return () => btns.forEach((b) => { b.removeEventListener("mousemove", onMove); b.removeEventListener("mouseleave", onLeave); });
  }, []);

  // Mouse parallax on hero text
  useEffect(() => {
    const heroText = document.querySelector(".hero-parallax");
    if (!heroText) return;
    const onMove = (e) => {
      const cx = (e.clientX / window.innerWidth - 0.5) * 18;
      const cy = (e.clientY / window.innerHeight - 0.5) * 10;
      heroText.style.transform = `translate(${cx}px, ${cy}px)`;
    };
    window.addEventListener("mousemove", onMove);
    return () => window.removeEventListener("mousemove", onMove);
  }, []);

  return (
    <>
      {/* Custom cursor */}
      <div ref={cursorDotRef} className="rv-cursor-dot" />
      <div ref={cursorRingRef} className="rv-cursor-ring" />

      {/* ── Hero ─────────────────────────────────────────────── */}
      <section className="rv-hero">
        <canvas ref={canvasRef} className="rv-hero-canvas" />

        {/* Radial glow behind headline */}
        <div className="rv-hero-glow" />

        <div className="rv-hero-inner">
          <div className="hero-parallax">
            <div className="rv-eyebrow rv-reveal">
              <span className="rv-pulse-dot" />
              AI-Powered Investing Platform
            </div>

            <h1 className="rv-headline rv-reveal rv-reveal-d1">
              Invest like a<br />
              <span className="rv-accent rv-shimmer">machine.</span>
            </h1>

            <p className="rv-subline rv-reveal rv-reveal-d2">
              RoboVest runs your trading algorithms 24/7, reacting to markets
              in real-time — no emotion, no hesitation, no sleep.
            </p>

            <div className="rv-hero-ctas rv-reveal rv-reveal-d3">
              <Link to="/signup" className="rv-btn-prime magnetic">
                Start trading free <span className="rv-btn-arrow">→</span>
              </Link>
              <a href="#how-it-works" className="rv-btn-ghost magnetic">
                See how it works
              </a>
            </div>
          </div>

          <div className="rv-scroll-hint rv-reveal rv-reveal-d4">
            <div className="rv-scroll-line" />
            <span>scroll</span>
          </div>
        </div>

        {/* Live ticker */}
        <div className="rv-ticker">
          <div className="rv-ticker-track">
            {[...TICKER_ITEMS, ...TICKER_ITEMS].map((t, i) => (
              <span key={i} className="rv-ticker-item">
                <span className="rv-ticker-dot" />
                {t}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats ────────────────────────────────────────────── */}
      <section className="rv-stats-section">
        <div className="rv-stats-grid">
          {[
            { prefix: "$", target: 2.4, suffix: "B", decimals: 1, label: "Assets automated" },
            { prefix: "", target: 847000, suffix: "+", decimals: 0, label: "Trades executed" },
            { prefix: "", target: 99.9, suffix: "%", decimals: 1, label: "Platform uptime" },
            { prefix: "", target: 12000, suffix: "+", decimals: 0, label: "Active investors" },
          ].map((s, i) => (
            <div key={i} className={`rv-stat rv-reveal rv-reveal-d${i}`}>
              <div className="rv-stat-value">
                {s.prefix}
                <span
                  className="rv-counter"
                  data-target={s.target}
                  data-suffix={s.suffix}
                  data-decimals={s.decimals}
                >
                  0
                </span>
              </div>
              <div className="rv-stat-label">{s.label}</div>
            </div>
          ))}
        </div>
      </section>

      {/* ── Features ─────────────────────────────────────────── */}
      <section className="rv-features-section" id="features">
        <div className="rv-section-wrap">
          <div className="rv-section-header rv-reveal">
            <div className="rv-section-eyebrow">Why RoboVest</div>
            <h2 className="rv-section-title">
              Built for the <span className="rv-accent">serious</span> investor
            </h2>
          </div>

          <div className="rv-features-grid">
            {features.map((f, i) => (
              <div key={i} className={`rv-feature-card tilt-card rv-reveal rv-reveal-d${i}`}>
                <div className="rv-card-glow" />
                <div className="rv-card-icon">{f.icon}</div>
                <h3 className="rv-card-title">{f.title}</h3>
                <p className="rv-card-desc">{f.description}</p>
                <div className="rv-card-line" />
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ─────────────────────────────────────── */}
      <section className="rv-how-section" id="how-it-works">
        <div className="rv-section-wrap">
          <div className="rv-section-header rv-reveal">
            <div className="rv-section-eyebrow">The Process</div>
            <h2 className="rv-section-title">
              Three steps to <span className="rv-accent">automated alpha</span>
            </h2>
          </div>

          <div className="rv-steps">
            {steps.map((s, i) => (
              <div key={i} className={`rv-step rv-reveal rv-reveal-d${i}`}>
                <div className="rv-step-num-wrap">
                  <span className="rv-step-num">0{s.number}</span>
                </div>
                <div className="rv-step-body">
                  <h3 className="rv-step-title">{s.title}</h3>
                  <p className="rv-step-desc">{s.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ──────────────────────────────────────────────── */}
      <section className="rv-cta-section">
        <div className="rv-cta-grid-bg" />
        <div className="rv-section-wrap rv-cta-inner">
          <div className="rv-cta-badge rv-reveal">Limited beta access</div>
          <h2 className="rv-cta-headline rv-reveal rv-reveal-d1">
            Your algorithms.<br />
            Your rules.<br />
            <span className="rv-accent rv-shimmer">Your edge.</span>
          </h2>
          <p className="rv-cta-sub rv-reveal rv-reveal-d2">
            Join thousands of investors who automate their strategies with RoboVest.
          </p>
          <div className="rv-reveal rv-reveal-d3">
            <Link to="/signup" className="rv-btn-prime rv-btn-prime-lg magnetic">
              Create free account <span className="rv-btn-arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* ── Footer ───────────────────────────────────────────── */}
      <footer className="rv-footer">
        <div className="rv-footer-inner">
          <div className="rv-footer-brand">
            <span className="rv-brand-dot" />
            RoboVest
          </div>
          <div className="rv-footer-copy">&copy; {new Date().getFullYear()} RoboVest. All rights reserved.</div>
          <div className="rv-footer-links">
            <Link to="/login">Login</Link>
            <Link to="/signup">Sign Up</Link>
          </div>
        </div>
      </footer>
    </>
  );
};

export default Home;
